import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from '../../translations/translations';
import { useContacts } from '../../contexts/ContactsContext';
import { contactsAPI } from '../../services/api';
import ContactDeleteDialog from './ContactDeleteDialog';
import ContactDetailsDialog from './ContactDetailsDialogRefactored';
import EditContactDialog from '../dialogs/EditContactDialog';

const ContactDialogsContext = createContext(null);

const CONTACT_EVENTS = ['openContactDetails'];
const EDIT_EVENTS = ['openContactEdit'];

export const useContactDialogs = () => {
  const context = useContext(ContactDialogsContext);
  if (!context) {
    throw new Error('useContactDialogs must be used within a ContactDialogProvider');
  }
  return context;
};

const getContactPayload = (detail = {}) => detail.contact || detail.contactId || detail.id;

const ContactDialogProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const t = translations.en;
  const {
    contacts,
    updateContact,
    deleteContact,
    archiveContact,
    unarchiveContact,
    addInteraction,
    loadContacts,
  } = useContacts();

  const [selectedContact, setSelectedContact] = useState(null);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, contactId: null, contactName: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const resolveContact = useCallback(async (contactOrId) => {
    if (!contactOrId) return null;

    const contactId = typeof contactOrId === 'string' ? contactOrId : contactOrId._id;
    const fallback = typeof contactOrId === 'object'
      ? contactOrId
      : contacts.find((contact) => contact._id === contactId);

    if (!contactId) return fallback || null;

    try {
      const response = await contactsAPI.getContact(contactId);
      return response.data || fallback || null;
    } catch (error) {
      console.error('Error loading contact:', error);
      return fallback || null;
    }
  }, [contacts]);

  const refreshSelectedContact = useCallback(async (contactId, fallback = null) => {
    const freshContact = await resolveContact(contactId);
    setSelectedContact(freshContact || fallback);
    return freshContact || fallback;
  }, [resolveContact]);

  const openContactDetails = useCallback(async (contactOrId, options = {}) => {
    const contact = await resolveContact(contactOrId);
    if (!contact) {
      showMessage(t.errorLoadingContact || 'Error loading contact', 'error');
      return;
    }

    setSelectedContact(contact);

    if (options.action === 'addInteraction' || options.addInteraction) {
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('trigger-add-interaction', {
          detail: {
            contactId: contact._id,
            suggestedNote: options.suggestedNote,
          },
        }));
      }, 250);
    }
  }, [resolveContact, showMessage, t]);

  const openEditContact = useCallback(async (contactOrId) => {
    const contact = await resolveContact(contactOrId);
    if (!contact) {
      showMessage(t.errorLoadingContact || 'Error loading contact', 'error');
      return;
    }
    setContactToEdit(contact);
  }, [resolveContact, showMessage, t]);

  const handleSaveEditedContact = useCallback(async (contactData) => {
    try {
      const updatedContact = await updateContact(contactData._id, contactData);
      setContactToEdit(null);
      setSelectedContact((current) => (
        current?._id === contactData._id ? updatedContact : current
      ));
      showMessage(t.contactUpdatedSuccessfully || 'Contact updated successfully!');
      return updatedContact;
    } catch (error) {
      console.error('Error updating contact:', error);
      showMessage(t.errorUpdatingContact || 'Error updating contact', 'error');
      throw error;
    }
  }, [showMessage, t, updateContact]);

  const handleDeleteContact = useCallback((contactId) => {
    const contact = selectedContact?._id === contactId
      ? selectedContact
      : contacts.find((item) => item._id === contactId);

    setDeleteConfirmation({
      open: true,
      contactId,
      contactName: contact?.name || '',
    });
  }, [contacts, selectedContact]);

  const confirmDeleteContact = useCallback(async () => {
    try {
      await deleteContact(deleteConfirmation.contactId);
      setSelectedContact(null);
      setDeleteConfirmation({ open: false, contactId: null, contactName: '' });
      showMessage(t.contactDeletedSuccessfully || 'Contact deleted successfully!');
    } catch (error) {
      console.error('Error deleting contact:', error);
      showMessage(t.errorDeletingContact || 'Error deleting contact', 'error');
    }
  }, [deleteConfirmation.contactId, deleteContact, showMessage, t]);

  const handleArchiveContact = useCallback(async (contact) => {
    try {
      await archiveContact(contact._id);
      setSelectedContact(null);
      if (loadContacts) await loadContacts();
      showMessage(t.contactArchivedSuccessfully || 'Contact archived successfully!');
    } catch (error) {
      console.error('Error archiving contact:', error);
      showMessage(t.errorArchivingContact || 'Error archiving contact', 'error');
    }
  }, [archiveContact, loadContacts, showMessage, t]);

  const handleUnarchiveContact = useCallback(async (contact) => {
    try {
      const updatedContact = await unarchiveContact(contact._id);
      setSelectedContact(updatedContact || null);
      if (loadContacts) await loadContacts();
      showMessage(t.contactUnarchivedSuccessfully || 'Contact unarchived successfully!');
    } catch (error) {
      console.error('Error unarchiving contact:', error);
      showMessage(t.errorUnarchivingContact || 'Error unarchiving contact', 'error');
    }
  }, [loadContacts, showMessage, t, unarchiveContact]);

  const handleAddInteraction = useCallback(async (contactId, interactionData) => {
    try {
      await addInteraction(contactId, interactionData);
      await refreshSelectedContact(contactId);
      showMessage(t.interactionAdded || 'Touch added');
    } catch (error) {
      console.error('Error adding touch:', error);
      showMessage(t.errorAddingInteraction || 'Error adding touch', 'error');
      throw error;
    }
  }, [addInteraction, refreshSelectedContact, showMessage, t]);

  const handleContactUpdate = useCallback((updatedContact) => {
    setSelectedContact(updatedContact);
  }, []);

  const handleViewRelatedContact = useCallback((contactOrId) => {
    openContactDetails(contactOrId);
  }, [openContactDetails]);

  useEffect(() => {
    const handleOpenDetails = (event) => {
      const payload = getContactPayload(event.detail);
      openContactDetails(payload, event.detail || {});
    };
    const handleOpenEdit = (event) => {
      openEditContact(getContactPayload(event.detail));
    };
    const handleOpenInteraction = (event) => {
      openContactDetails(getContactPayload(event.detail), {
        ...(event.detail || {}),
        addInteraction: true,
      });
    };

    CONTACT_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleOpenDetails);
      document.addEventListener(eventName, handleOpenDetails);
    });
    EDIT_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleOpenEdit);
      document.addEventListener(eventName, handleOpenEdit);
    });
    document.addEventListener('open-interaction-dialog', handleOpenInteraction);

    return () => {
      CONTACT_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleOpenDetails);
        document.removeEventListener(eventName, handleOpenDetails);
      });
      EDIT_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleOpenEdit);
        document.removeEventListener(eventName, handleOpenEdit);
      });
      document.removeEventListener('open-interaction-dialog', handleOpenInteraction);
    };
  }, [openContactDetails, openEditContact]);

  useEffect(() => {
    if (location.pathname !== '/contacts') return;

    const params = new URLSearchParams(location.search);
    const contactId = params.get('contactId');
    if (!contactId) return;

    openContactDetails(contactId, {
      action: params.get('action'),
    });

    params.delete('contactId');
    params.delete('action');
    navigate(params.toString() ? `/contacts?${params.toString()}` : '/contacts', { replace: true });
  }, [location.pathname, location.search, navigate, openContactDetails]);

  const value = useMemo(() => ({
    openContactDetails,
    openEditContact,
    closeContactDetails: () => setSelectedContact(null),
    closeEditContact: () => setContactToEdit(null),
  }), [openContactDetails, openEditContact]);

  return (
    <ContactDialogsContext.Provider value={value}>
      {children}

      <ContactDetailsDialog
        open={selectedContact !== null}
        contact={selectedContact}
        onClose={() => setSelectedContact(null)}
        onEdit={openEditContact}
        onDelete={handleDeleteContact}
        onArchive={handleArchiveContact}
        onUnarchive={handleUnarchiveContact}
        onAddInteraction={handleAddInteraction}
        onViewRelatedContact={handleViewRelatedContact}
        onContactUpdate={handleContactUpdate}
        allContacts={contacts}
        t={t}
      />

      <EditContactDialog
        open={contactToEdit !== null}
        contact={contactToEdit}
        onClose={() => setContactToEdit(null)}
        onSave={handleSaveEditedContact}
        t={t}
      />

      <ContactDeleteDialog
        confirmation={deleteConfirmation}
        onCancel={() => setDeleteConfirmation({ open: false, contactId: null, contactName: '' })}
        onConfirm={confirmDeleteContact}
        t={t}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ContactDialogsContext.Provider>
  );
};

export default ContactDialogProvider;
